import { gql } from '@apollo/client';
import IconCollapseList from '@apollo/icons/default/IconCollapseList.svg';
import IconDiff from '@apollo/icons/default/IconDiff.svg';
import IconExpandList from '@apollo/icons/default/IconExpandList.svg';
import IconSingleService from '@apollo/icons/default/IconSingleService.svg';
import IconTime from '@apollo/icons/default/IconTime.svg';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardHeaderControl,
  Group,
  IconButton,
  Text,
} from '@apollo/orbit';
import React, { useState } from 'react';

import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import {
  LocalTimestamp,
  RelativeTimestamp,
} from 'src/components/timestamp/Timestamp';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { safeCompileMarkdown } from 'src/lib/safeCompileMarkdown';

import { ChangesDiffs } from '../../proposalChanges/ChangesDiffs';

export const ActivityFeedRevisionFragment = gql`
  fragment ActivityFeedRevisionFragment on ProposalRevision {
    id
    summary
    launch @include(if: $canQueryLaunches) {
      id
      createdAt
      buildInput {
        ... on CompositionBuildInput {
          subgraphs {
            hash
            name
          }
        }
      }
    }
    previousRevision @include(if: $canQueryLaunches) {
      id
      launch {
        id
        createdAt
        buildInput {
          ... on CompositionBuildInput {
            subgraphs {
              hash
              name
            }
          }
        }
      }
    }
  }
`;

export const ActivityFeedRevision = ({
  proposalStatus,
  target,
  previousLaunch,
  node,
  proposal,
  graphRef,
  className,
  canQueryLaunches,
  sourceGraphVariant,
}: {
  proposalStatus: GraphQLTypes.ProposalStatus;
  target: GraphQLTypes.ActivityFeedRevisionFragment;
  previousLaunch: GraphQLTypes.ActivityFeedFirstLaunchGraphVariantFragment_launchHistory;
  node: GraphQLTypes.ActivityFeedProposalFragment_activities_edges_node;
  proposal: GraphQLTypes.UseAddCommentProposalFragment;
  graphRef: GraphRef;
  className?: string;
  canQueryLaunches: boolean;
  sourceGraphVariant: string | undefined;
}) => {
  const [changesExpanded, setChangesExpanded] = useState(false);

  const subgraphs =
    target.launch?.buildInput.__typename === 'CompositionBuildInput'
      ? target.launch?.buildInput.subgraphs
      : [];
  const previousSubgraphs =
    previousLaunch.buildInput.__typename === 'CompositionBuildInput'
      ? previousLaunch.buildInput.subgraphs
      : [];

  const removedSubgraphs = previousSubgraphs.filter(
    (previousSubgraph) =>
      !subgraphs.find((s) => s.name === previousSubgraph.name),
  );

  const addedOrChangedSubgraphs = subgraphs.filter(
    (subgraph) =>
      subgraph.hash !==
      previousSubgraphs.find((s) => s.name === subgraph.name)?.hash,
  );

  const allModifiedSubgraphs = [
    ...removedSubgraphs,
    ...addedOrChangedSubgraphs,
  ];
  const totalChanged = allModifiedSubgraphs.length;

  const currentRevisionId = target.id;
  return (
    <div className="mt-3 flex items-start">
      <div className="z-10 mr-4 mt-2 flex h-10 min-w-10 items-center justify-center rounded-full border border-primary bg-primary">
        <IconDiff />
      </div>
      <Card isDivided className={className}>
        <CardHeader>
          <Group>
            {node.createdBy?.__typename === 'User' ? (
              <Avatar
                size="sm"
                name={node.createdBy.fullName}
                src={node.createdBy.avatarUrl ?? undefined}
              />
            ) : (
              <Avatar size="sm" />
            )}
            <CardDescription>
              <Text as="span" variant="bodySb">
                {node.createdBy?.__typename === 'User'
                  ? node.createdBy.fullName
                  : 'Unknown'}
              </Text>{' '}
              made changes to <IconSingleService className="align-middle" />{' '}
              <Text as="span" variant="bodySb" className="text-neutral">
                {allModifiedSubgraphs[0]?.name}
              </Text>{' '}
              {totalChanged > 1 && (
                <>
                  and{' '}
                  <Tooltip
                    label={
                      <div className="flex flex-wrap gap-2">
                        {allModifiedSubgraphs.slice(1).map(({ name }) => (
                          <span key={name} className="whitespace-nowrap">
                            <IconSingleService className="mr-1 align-middle" />
                            <Text as="span" variant="bodySb" size="sm">
                              {name}
                            </Text>
                          </span>
                        ))}
                      </div>
                    }
                    placement="bottom-start"
                    shouldWrapChildren
                  >
                    <Text as="span" variant="bodySb" className="cursor-pointer">
                      {totalChanged - 1} more{' '}
                      {format.pluralize(totalChanged - 1, 'subgraph')}
                    </Text>
                  </Tooltip>
                </>
              )}
            </CardDescription>
            {currentRevisionId === proposal.latestRevision.id && (
              <Badge>LATEST</Badge>
            )}
          </Group>
          <CardHeaderControl>
            <RelativeTimestamp
              value={node.createdAt}
              className="text-secondary max-md:hidden"
            />
            <LocalTimestamp
              value={node.createdAt}
              className="rounded p-2 hover:bg-primary md:hidden"
            >
              <IconTime className="block text-icon-primary" />
            </LocalTimestamp>
          </CardHeaderControl>
        </CardHeader>
        <CardBody>
          {target.summary && (
            <p className="text-secondary">
              {safeCompileMarkdown(target.summary)}
            </p>
          )}
          {changesExpanded ? (
            <>
              <div className="mb-4">
                <Button
                  variant="secondary"
                  leftIcon={<IconDiff />}
                  onClick={() => setChangesExpanded(false)}
                >
                  Collapse all changes
                </Button>
              </div>
              {removedSubgraphs.map(({ name, hash }) => (
                <SubgraphRevision
                  proposalStatus={proposalStatus}
                  canQueryLaunches={canQueryLaunches}
                  sourceGraphVariant={sourceGraphVariant}
                  subgraphName={name}
                  key={name}
                  graphRef={graphRef}
                  oldSchemaHash={hash}
                  newSchemaHash={undefined}
                />
              ))}
              {addedOrChangedSubgraphs.map(({ name, hash }) => (
                <SubgraphRevision
                  proposalStatus={proposalStatus}
                  canQueryLaunches={canQueryLaunches}
                  sourceGraphVariant={sourceGraphVariant}
                  subgraphName={name}
                  key={name}
                  graphRef={graphRef}
                  oldSchemaHash={
                    previousSubgraphs.find((s) => s.name === name)?.hash
                  }
                  newSchemaHash={hash}
                />
              ))}
            </>
          ) : (
            <div>
              <Button
                variant="secondary"
                leftIcon={<IconDiff />}
                onClick={() => setChangesExpanded(true)}
              >
                Expand all changes
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

const SubgraphRevision = ({
  proposalStatus,
  subgraphName,
  graphRef,
  oldSchemaHash,
  newSchemaHash,
  canQueryLaunches,
  sourceGraphVariant,
}: {
  proposalStatus: GraphQLTypes.ProposalStatus;
  subgraphName: string;
  graphRef: GraphRef;
  oldSchemaHash?: string;
  newSchemaHash?: string;
  canQueryLaunches: boolean;
  sourceGraphVariant: string | undefined;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [individualCollapsedState, setIndividualCollapsedState] = useState<
    Record<string, boolean>
  >({});

  return (
    <div>
      <div className="flex items-center text-lg font-semibold text-neutral">
        <IconSingleService className="mr-1 text-icon-primary" />
        {subgraphName}
        <Tooltip
          label={
            isCollapsed ? 'Expand subgraph diff' : 'Collapse subgraph diff'
          }
        >
          <IconButton
            size="sm"
            variant="hidden"
            className="ml-2"
            aria-label={
              isCollapsed ? 'Expand subgraph diff' : 'Collapse subgraph diff'
            }
            onClick={() => setIsCollapsed((value) => !value)}
            icon={isCollapsed ? <IconExpandList /> : <IconCollapseList />}
          />
        </Tooltip>
      </div>
      {!isCollapsed && (
        <ChangesDiffs
          proposalStatus={proposalStatus}
          canQueryLaunches={canQueryLaunches}
          sourceGraphVariant={sourceGraphVariant}
          implementedChanges={[]} // don't show implemented indicators in homepage view
          includeImplementedChanges
          graphRef={graphRef}
          oldSchemaHash={oldSchemaHash}
          newSchemaHash={newSchemaHash}
          collapsedState={individualCollapsedState}
          setCollapsedState={setIndividualCollapsedState}
          className="mt-4 space-y-6 border-l border-primary pl-4"
          canMakeComments={false}
        />
      )}
    </div>
  );
};
